import { useEffect, useRef } from 'react'
import type { RcFile } from 'antd/es/upload'
import {
  loadPendingAttachments,
  savePendingAttachments,
} from './composerOutbox'

interface UsePendingAttachmentsArgs {
  // Identifies the composer whose selection this is: conversation+mode for the
  // reply composer (see composerKey), a constant for the new-conversation modal.
  key: string
  attachments: RcFile[]
  replace: (files: RcFile[]) => void
}

// Keeps a composer's picked-but-unsent attachments in sync with the persistent
// outbox, so a failed send, a reload or a conversation switch never loses the
// operator's file. Shared by the reply composer and the new-conversation
// modal, which need identical — and identically subtle — restore/persist
// behaviour.
//
// The ordering guard is the whole point: the persist effect must not run until
// the restore for the current key has landed, or it would fire with an empty
// selection and delete the very record being restored.
export function usePendingAttachments({
  key,
  attachments,
  replace,
}: UsePendingAttachmentsArgs): void {
  // The key whose persisted files are currently loaded; null while a restore is
  // in flight. Also stops a switch from writing the previous composer's files
  // under the new key.
  const restoredKeyRef = useRef<string | null>(null)

  useEffect(() => {
    let cancelled = false
    restoredKeyRef.current = null
    replace([])
    loadPendingAttachments(key).then((files) => {
      if (cancelled) {
        return
      }
      if (files.length > 0) {
        replace(files)
      }
      restoredKeyRef.current = key
    })
    return () => {
      cancelled = true
    }
    // `replace` is a stable setState wrapper; only the key drives a reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  useEffect(() => {
    if (restoredKeyRef.current !== key) {
      return
    }
    void savePendingAttachments(key, attachments)
  }, [key, attachments])
}
